/**
 * Submit Form Page
 *
 * Public form submission page for the signer portal.
 * Features:
 * - Form rendering from JSON schema
 * - All 15 field types supported
 * - Validation
 * - File upload to Azure Blob Storage
 * - Success confirmation with redirect option
 * - Rate limiting handled server-side
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { Button, Card, CardContent } from '@respectly/ui';
import { executeRecaptcha } from '@respectly/utils';
import { PortalLayout } from '../layouts/PortalLayout';
import { FormRenderer } from '../components/FormRenderer';
import { NotFoundPage } from './NotFoundPage';
import { LoadingPage } from './LoadingPage';
import type { FormField, FormSettings } from '@respectly/shared-types';

interface FormData {
  id: string;
  name: string;
  description?: string;
  schema: {
    fields: FormField[];
    layout?: 'single' | 'two-column';
  };
  settings: FormSettings;
  branding?: {
    logoUrl?: string;
    primaryColor?: string;
    companyName?: string;
    logoPosition?: 'left' | 'center' | 'right';
    inputBorderRadius?: number;
    inputBorderColor?: string;
    inputBackgroundColor?: string;
    inputTextColor?: string;
  };
}

interface SubmissionResult {
  success: boolean;
  submissionId?: string;
  message: string;
  redirectUrl?: string;
}

export function SubmitForm() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);

  // Get form URL from path
  const getFormUrl = useCallback(() => {
    const pathParts = window.location.pathname.split('/');
    const formIndex = pathParts.indexOf('form');

    // Check if it's a token-based URL: /form/:formId/token/:token
    if (pathParts.includes('token')) {
      const formId = pathParts[formIndex + 1];
      const tokenIndex = pathParts.indexOf('token');
      const token = pathParts[tokenIndex + 1];
      return { type: 'token', formId, token };
    }

    // Otherwise it's a public URL: /form/:publicUrl
    const publicUrl = pathParts[formIndex + 1];
    return { type: 'public', publicUrl };
  }, []);

  // Fetch form data
  useEffect(() => {
    const fetchForm = async () => {
      const urlInfo = getFormUrl();

      try {
        let response: Response;

        if (urlInfo.type === 'token') {
          response = await fetch(
            `/public/form/${urlInfo.formId}/token/${urlInfo.token}`
          );
        } else {
          response = await fetch(`/public/form/${urlInfo.publicUrl}`);
        }

        const result = await response.json();

        if (!response.ok || !result.success) {
          if (response.status === 404) {
            setError('not_found');
          } else {
            setError(result.error?.message || 'Failed to load form');
          }
          setIsLoading(false);
          return;
        }

        // Check if this is an NDF form - redirect to NDF form page
        if (result.data?.ndfDocument) {
          const publicUrl = urlInfo.type === 'public' ? urlInfo.publicUrl : '';
          if (publicUrl) {
            window.location.href = `/form-ndf/${publicUrl}`;
            return;
          }
        }

        setFormData(result.data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching form:', err);
        setError('Failed to connect to server. Please try again.');
        setIsLoading(false);
      }
    };

    fetchForm();
  }, [getFormUrl]);

  // Handle file upload
  const handleFileUpload = async (file: File): Promise<string> => {
    const urlInfo = getFormUrl();
    const publicUrl =
      urlInfo.type === 'public'
        ? urlInfo.publicUrl
        : formData?.id || urlInfo.formId;

    const formDataToSend = new FormData();
    formDataToSend.append('file', file);

    const response = await fetch(`/public/form/${publicUrl}/upload`, {
      method: 'POST',
      body: formDataToSend,
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to upload file');
    }

    return result.data.fileUrl;
  };

  // Handle form submission
  const handleSubmit = async (data: Record<string, unknown>) => {
    const urlInfo = getFormUrl();
    const publicUrl =
      urlInfo.type === 'public' ? urlInfo.publicUrl : formData?.id;

    setIsSubmitting(true);
    setError(null);

    try {
      const recaptchaToken = await executeRecaptcha('FORM_SUBMIT');
      const response = await fetch(`/public/form/${publicUrl}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data, recaptchaToken }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error?.message || 'Failed to submit form');
        setIsSubmitting(false);
        return;
      }

      setSubmissionResult({
        success: true,
        submissionId: result.data.submissionId,
        message:
          result.data.message ||
          formData?.settings?.successMessage ||
          'Thank you for your submission!',
        redirectUrl: result.data.redirectUrl || formData?.settings?.redirectUrl,
      });
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Render based on state
  if (isLoading) {
    return <LoadingPage message="Loading form..." />;
  }

  if (error === 'not_found') {
    return <NotFoundPage type="form" />;
  }

  // Get primary color for branding
  const primaryColor = formData?.branding?.primaryColor || '#3b82f6';

  if (error) {
    return (
      <PortalLayout showHeader={false}>
        <div className="relative flex flex-1 items-center justify-center p-4 overflow-hidden">
          {/* Layered gradient background */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />

          <Card className="relative z-10 w-full max-w-md border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden">
            <div
              className="h-1"
              style={{
                background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
              }}
            />
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Error</h1>
              <p className="mt-3 text-gray-600">{error}</p>
              <Button
                variant="outline"
                className="mt-6"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  if (!formData) {
    return <NotFoundPage type="form" />;
  }

  // Success state
  if (submissionResult?.success) {
    return (
      <PortalLayout showHeader={false}>
        <div className="relative flex flex-1 items-center justify-center p-4 overflow-hidden">
          {/* Layered gradient background */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative z-10 w-full max-w-md"
          >
            {/* Glass-morphism card */}
            <Card className="border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden">
              {/* Accent bar */}
              <div
                className="h-1"
                style={{
                  background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
                }}
              />
              <CardContent className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <CheckCircle2 className="h-10 w-10" style={{ color: primaryColor }} />
                </motion.div>

                <h1 className="text-2xl font-bold text-gray-900">
                  Submitted!
                </h1>

                <p className="mt-3 text-gray-600">{submissionResult.message}</p>

                <div className="mt-8 space-y-3">
                  {submissionResult.redirectUrl && (
                    <Button
                      variant="default"
                      className="w-full"
                      style={{
                        backgroundColor: primaryColor,
                        borderColor: primaryColor,
                      }}
                      onClick={() => {
                        if (submissionResult.redirectUrl) {
                          window.location.href = submissionResult.redirectUrl;
                        }
                      }}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Continue
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { window.close(); setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 300); }}
                  >
                    Close this page
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </PortalLayout>
    );
  }

  // Main form view
  return (
    <PortalLayout showHeader={false}>
      {/* Layered gradient background */}
      <div className="relative flex-1 overflow-y-auto">
        {/* Base gradient layer */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
          }}
        />
        {/* Radial accent - creates depth */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
          }}
        />
        {/* Subtle bottom accent */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
          }}
        />
        {/* Subtle noise texture overlay for premium feel */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Content */}
        <div className="relative z-10 mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
          {/* Form Header */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            {/* Logo if available */}
            {formData.branding?.logoUrl && (
              <div
                className={`mb-6 flex ${
                  formData.branding.logoPosition === 'left'
                    ? 'justify-start'
                    : formData.branding.logoPosition === 'right'
                    ? 'justify-end'
                    : 'justify-center'
                }`}
              >
                <img
                  src={formData.branding.logoUrl}
                  alt={formData.branding.companyName || 'Company logo'}
                  className="h-16 w-auto object-contain"
                />
              </div>
            )}

            <div className="flex items-center gap-3">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <FileText className="h-6 w-6" style={{ color: primaryColor }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {formData.name}
                </h1>
                {formData.branding?.companyName && (
                  <p className="text-sm text-gray-500">
                    by {formData.branding.companyName}
                  </p>
                )}
              </div>
            </div>

            {formData.description && (
              <p className="mt-4 text-gray-600">{formData.description}</p>
            )}
          </motion.div>

          {/* Form Content */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {/* Glass-morphism card */}
            <Card className="border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden">
              {/* Accent bar */}
              <div
                className="h-1"
                style={{
                  background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
                }}
              />
              <CardContent className="p-8 sm:p-10">
                <FormRenderer
                  fields={formData.schema.fields}
                  layout={formData.schema.layout}
                  onSubmit={handleSubmit}
                  submitButtonText={formData.settings?.submitButtonText || 'Submit'}
                  isSubmitting={isSubmitting}
                  onFileUpload={handleFileUpload}
                  inputStyle={{
                    borderRadius: formData.branding?.inputBorderRadius,
                    borderColor: formData.branding?.inputBorderColor,
                    backgroundColor: formData.branding?.inputBackgroundColor,
                    textColor: formData.branding?.inputTextColor,
                  }}
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Privacy Notice */}
          <p className="mt-6 text-center text-xs text-gray-400">
            By submitting this form, you agree to our privacy policy and terms of
            service.
          </p>
        </div>
      </div>
    </PortalLayout>
  );
}
